import React, { useContext, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';
import { DateTime } from 'luxon';

import useMetricAggregationRequest from 'hooks/useMetricAggregationRequest';
import DateInputContext from 'contexts/DateInputContext';
import GqlClientContext from 'contexts/GqlClientContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import EntityDetailsContext from 'screens/EntityDetailsShow/EntityDetailsContext';
import { useReportGadgetRequest } from 'contextProviders/GadgetStatusBoundary';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import aggregateMetricMatrix from 'api/aggregateMetricMatrix';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import { ArrowBackdrop } from 'components/Gadget/Delta';
import {
  EVEN_ROW_HOVER,
  ODD_ROW_COLOR,
  ODD_ROW_HOVER,
  SPAN_CSS,
} from 'components/Grid/StyleOverrides';
import fetchRecords from 'components/PerformanceBoards/PerformanceBoardSlideOut/Sections/RelatedDocuments/fetchRecords';
import FlexCentered from 'components/Common/FlexCentered';
import Tooltip from 'components/Tooltip';
import useFilterInput from 'hooks/useFilterInput';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import useNetworkingEffect from 'hooks/useNetworkingEffect';
import useDateScope from 'hooks/useDateScope';
import usePopupCallback from 'hooks/usePopupCallback';
import useToMetricInput from 'hooks/useToMetricInput';
import { toFilterPlate } from 'migrateScope';
import useValueFormatters from 'hooks/useValueFormatters';
import metricTypeCheckers from 'types/metricTypeCheckers';
import { CardSkeleton } from 'components/Skeleton';

const Card = styled.div`
  background-color: var(--color-bg-secondary);
  border-radius: 8px;
  padding: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  tbody tr:nth-child(even) {
    background-color: ${ODD_ROW_COLOR};
  }

  tbody tr:nth-child(odd):hover {
    background-color: ${EVEN_ROW_HOVER};
  }

  tbody tr:nth-child(even):hover {
    background-color: ${ODD_ROW_HOVER};
  }
`;

const Td = styled.td`
  padding: 0 12px;
  border-bottom: 1px solid var(--color-grid-border-row);
  vertical-align: middle;
`;

const LabelTd = styled(Td)`
  width: 40%;
  border-right: 1px solid var(--color-grid-border-row);
`;

const ValueTd = styled(Td)``;

const CellSpan = styled.span`
  ${SPAN_CSS}
`;

interface TrendPoint {
  value: number;
  category: string;
}

interface TrendData {
  points: TrendPoint[];
  isUp: boolean;
}

interface RowResult {
  key: string;
  label: string;
  formattedValue: string | number | null;
  rawValue?: unknown;
  trendData?: TrendData;
}

const buildTrendDateScope = ({
  interval,
  numberOfIntervals,
  dateField,
  visEndDate,
}: VisualisationDefinitions.EntityInformationTableMetricRowTrending & {
  dateField?: string;
  visEndDate?: string;
}): DateRangeInput => {
  const anchor = visEndDate
    ? DateTime.fromISO(visEndDate, { zone: 'utc' })
    : DateTime.utc();
  const durationKey = `${interval}s` as
    | 'years'
    | 'quarters'
    | 'months'
    | 'weeks'
    | 'days'
    | 'hours';
  const startDate = anchor
    .minus({ [durationKey]: numberOfIntervals })
    .toISODate();
  const endDate = anchor.toISODate();
  return {
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
    dateField,
  };
};

const formatTrendCategory = ({
  isoDate,
  interval,
}: {
  isoDate: string;
  interval: FleetOps.NonAutoInterval;
}): string => {
  if (!isoDate) return '';
  const m = isoDate.substring(0, 10); // 'YYYY-MM-DD'
  const [year, month] = m.split('-');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const shortYear = `'${year.substring(2)}`;
  const monthName = monthNames[parseInt(month, 10) - 1] ?? month;
  if (interval === 'year') return year;
  if (interval === 'quarter') return `${monthName} ${shortYear}`;
  if (interval === 'month') return `${monthName} ${shortYear}`;
  if (interval === 'week' || interval === 'day') {
    return `${monthName} ${m.split('-')[2]}`;
  }
  return isoDate;
};

const TrendSparkline = ({
  trendData,
  metricId,
  label,
  trending,
  formatMetric,
}: {
  trendData: TrendData;
  metricId: string;
  label: string;
  trending: VisualisationDefinitions.EntityInformationTableMetricRowTrending;
  formatMetric: (args: { value: number; metricId: string }) => string | number;
}) => {
  const title = `${label} - Last ${trending.numberOfIntervals} ${trending.interval}${trending.numberOfIntervals === 1 ? '' : 's'}`;
  const options = useMemo((): Highcharts.Options => {
    return {
      accessibility: { enabled: false },
      chart: {
        type: 'column',
        width: 240,
        height: 160,
        backgroundColor: 'transparent',
        margin: [8, 8, 48, 48],
        animation: false,
      },
      title: { text: undefined },
      legend: { enabled: false },
      credits: { enabled: false },
      xAxis: {
        categories: trendData.points.map((p) => p.category),
        labels: {
          style: {
            color: 'var(--color-text-primary)',
            fontSize: '11px',
            whiteSpace: 'nowrap',
          },
          step: Math.ceil(trendData.points.length / 4),
          rotation: -35,
        },
        tickColor: 'transparent',
        lineColor: 'transparent',
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: 'var(--color-grey-8)',
        startOnTick: false,
        labels: {
          style: { color: 'var(--color-text-muted)', fontSize: '11px' },
          formatter: function () {
            return String(
              formatMetric({ value: this.value as number, metricId }),
            );
          },
        },
      },
      tooltip: { enabled: false },
      plotOptions: {
        column: {
          color: trendData.isUp
            ? 'var(--color-status-success)'
            : 'var(--color-status-danger)',
          borderRadius: 2,
          animation: false,
        },
      },
      series: [
        {
          type: 'column',
          data: trendData.points.map((p) => p.value),
        },
      ],
    };
  }, [trendData, metricId, formatMetric]);

  return (
    <div>
      <div
        style={{
          height: 40,
          padding: '0px 8px',
          borderBottom: '1px solid var(--color-border-secondary)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography.Header type="H6" isEllipsis>
          {title}
        </Typography.Header>
      </div>
      <div style={{ padding: '8px 8px 0px 0px' }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

const RowItem = ({
  row,
  result,
  renderMode,
  rowHeight,
  formatMetric,
}: {
  row: VisualisationDefinitions.EntityInformationTableRow;
  result: RowResult;
  renderMode: 'grid' | 'cards';
  rowHeight: number;
  formatMetric: (args: { value: number; metricId: string }) => string | number;
}) => {
  const entityDetails = useContext(EntityDetailsContext);
  const { dateField, dateRange, relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  const dateScope = useDateScope({});

  const fieldDrillDown = useMemo((): FilterPlateType | undefined => {
    if (row.type !== 'field' || typeof result.rawValue !== 'string') {
      return undefined;
    }
    const filter: TextFilter = {
      id: 'na',
      field: row.field,
      mode: 'plate',
      fieldType: 'text',
      keywordValues: [result.rawValue],
    };
    return toFilterPlate(filter);
  }, [row, result.rawValue]);

  const unSavedFilter = useMemo(
    (): UnSavedFilter => ({
      drillDowns: fieldDrillDown ? [fieldDrillDown] : [],
      scope: entityDetails ? entityDetails.entityFilterAsFilterPlates : [],
      dateScope,
      dateField,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
    }),
    [
      entityDetails,
      fieldDrillDown,
      dateScope,
      dateField,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
    ],
  );

  const onClicked = usePopupCallback(row, unSavedFilter);

  const trendIndicator = result.trendData && row.type === 'metric' && (
    <Tooltip
      isAltTooltip
      content={
        <TrendSparkline
          trendData={result.trendData}
          metricId={row.metricId}
          label={result.label}
          trending={row.trending!}
          formatMetric={formatMetric}
        />
      }
    >
      <ArrowBackdrop isGood={result.trendData.isUp} isGrid>
        <Icon
          icon={result.trendData.isUp ? 'arrow-up' : 'arrow-down'}
          color={
            result.trendData.isUp
              ? 'var(--color-status-success)'
              : 'var(--color-status-danger)'
          }
          width={12}
          height={12}
        />
      </ArrowBackdrop>
    </Tooltip>
  );

  if (renderMode === 'grid') {
    return (
      <tr style={{ height: rowHeight }}>
        <LabelTd>
          <CellSpan>{result.label}</CellSpan>
        </LabelTd>
        <ValueTd
          onClick={onClicked ? () => onClicked() : undefined}
          style={{ cursor: onClicked ? 'pointer' : undefined }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CellSpan
              style={{
                color: onClicked ? 'var(--color-text-link)' : undefined,
              }}
            >
              {result.formattedValue !== null &&
              result.formattedValue !== undefined
                ? result.formattedValue
                : '-'}
            </CellSpan>
            {trendIndicator}
          </div>
        </ValueTd>
      </tr>
    );
  }

  return (
    <Card>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        {result.label}
      </Typography.Body>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginTop: 4,
          cursor: onClicked ? 'pointer' : undefined,
        }}
        onClick={onClicked ? () => onClicked() : undefined}
      >
        <Typography.Body
          type="Label"
          color={onClicked ? 'var(--color-text-link)' : undefined}
        >
          {result.formattedValue !== null && result.formattedValue !== undefined
            ? result.formattedValue
            : '-'}
        </Typography.Body>
        {trendIndicator}
      </div>
    </Card>
  );
};

const EntityInformationTableContent = ({
  gadget,
}: {
  gadget: VisualisationDefinitions.EntityInformationTable;
}) => {
  const entityDetails = useContext(EntityDetailsContext);
  const { rowHeight } = useContext(RowHeightContext);
  const { client } = useContext(GqlClientContext);
  const { metricOptions } = useContext(MetricOptionsContext);
  const { resolveMetricForRequest } = useMetricAggregationRequest();
  const { filterInput } = useFilterInput({});
  const dateScope = useDateScope({});
  const toMetricInput = useToMetricInput();
  const { formatMetric, formatField } = useValueFormatters();
  const { getFieldLabel } = useGetFieldLabel();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [results, setResults] = useState<RowResult[]>([]);
  // Its requests handle their own failures (a row shows no value)
  const reportRequest = useReportGadgetRequest();

  const fieldRows = useMemo(
    () =>
      gadget.rows.filter(
        (r): r is VisualisationDefinitions.EntityInformationTableFieldRow =>
          r.type === 'field',
      ),
    [gadget.rows],
  );

  const metricRows = useMemo(
    () =>
      gadget.rows.filter(
        (r): r is VisualisationDefinitions.EntityInformationTableMetricRow =>
          r.type === 'metric',
      ),
    [gadget.rows],
  );

  const args = useMemo(() => {
    if (!entityDetails || gadget.rows.length === 0) return undefined;
    return {
      client,
      fieldRows,
      metricRows,
      allRows: gadget.rows,
      metricOptions,
      filterInput,
      entityFilter: entityDetails.entityFilter,
      dateScope,
    };
  }, [
    entityDetails,
    client,
    fieldRows,
    metricRows,
    gadget.rows,
    metricOptions,
    filterInput,
    dateScope,
  ]);

  useNetworkingEffect(() => {
    if (!args) {
      setIsLoading(false);
      setResults([]);
      reportRequest(undefined);
      return;
    }

    let isActive = true;
    setIsLoading(true);
    reportRequest('loading');

    const run = async () => {
      // Group field rows by datasetId and fetch one document per unique dataset
      const rowsByDataset = new Map<
        string,
        VisualisationDefinitions.EntityInformationTableFieldRow[]
      >();
      args.fieldRows.forEach((row) => {
        if (!row.datasetId) return;
        rowsByDataset.set(row.datasetId, [
          ...(rowsByDataset.get(row.datasetId) ?? []),
          row,
        ]);
      });

      const documentByDataset = new Map<string, ElasticDocument | undefined>();
      await Promise.all(
        Array.from(rowsByDataset.keys()).map(async (datasetId) => {
          try {
            const datasetSort = gadget.datasetSorts?.[datasetId];
            const sortBy: SortField | undefined = datasetSort?.sortByField
              ? {
                  field: datasetSort.sortByField,
                  sort: datasetSort.sortByDirection ?? 'desc',
                }
              : undefined;
            const docs = await fetchRecords({
              dataType: datasetId,
              filters: [args.entityFilter, args.filterInput],
              dateScope: args.dateScope,
              client: args.client,
              sortBy,
            });
            documentByDataset.set(
              datasetId,
              docs.length > 0 ? docs[0] : undefined,
            );
          } catch (e) {
            documentByDataset.set(datasetId, undefined);
          }
        }),
      );

      // Fetch metric values and trend data concurrently
      const metricResultMap = new Map<string, number | null>();
      const trendDataMap = new Map<string, TrendData | undefined>();
      const trendingRows = args.metricRows.filter((row) => !!row.trending);

      await Promise.all([
        Promise.all(
          args.metricRows.map(async (row) => {
            const metric = args.metricOptions.find(
              (m) => m.id === row.metricId,
            );
            if (!metric) {
              metricResultMap.set(row.key, null);
              return;
            }
            try {
              if (metricTypeCheckers.isCompoundMetric(metric)) {
                const resolved = resolveMetricForRequest(metric);
                // Not ready → an input metric is still loading; skip rather
                // than ship a partial request (backend "parse error").
                if (!resolved.ready || !resolved.expression) {
                  metricResultMap.set(row.key, null);
                  return;
                }
                const metrics = resolved.metricInputs;
                const expressions: Expression2[] = [resolved.expression];
                const response = await aggregateMetricMatrix({
                  client: args.client,
                  metrics,
                  expressions,
                  filterInput: [args.entityFilter, args.filterInput],
                  dateScope: args.dateScope,
                });
                const value =
                  response.length > 0 ? response[0][row.metricId] : null;
                metricResultMap.set(row.key, value as number | null);
              } else if (metricTypeCheckers.isNormalMetric(metric)) {
                const response = await aggregateMetricMatrix({
                  client: args.client,
                  metrics: [toMetricInput(metric)],
                  expressions: [],
                  filterInput: [args.entityFilter, args.filterInput],
                  dateScope: args.dateScope,
                });
                const value =
                  response.length > 0 ? response[0][row.metricId] : null;
                metricResultMap.set(row.key, value as number | null);
              }
            } catch (e) {
              metricResultMap.set(row.key, null);
            }
          }),
        ),
        Promise.all(
          trendingRows.map(async (row) => {
            const trending =
              row.trending as VisualisationDefinitions.EntityInformationTableMetricRowTrending;
            const metric = args.metricOptions.find(
              (m) => m.id === row.metricId,
            );
            if (!metric) {
              trendDataMap.set(row.key, undefined);
              return;
            }
            try {
              const trendDateScope = buildTrendDateScope({
                ...trending,
                dateField: args.dateScope.dateField,
                visEndDate: args.dateScope.endDate,
              });
              let response: MetricResponse[];
              if (metricTypeCheckers.isCompoundMetric(metric)) {
                const resolved = resolveMetricForRequest(metric);
                if (!resolved.ready || !resolved.expression) {
                  trendDataMap.set(row.key, undefined);
                  return;
                }
                const metrics = resolved.metricInputs;
                const expressions: Expression2[] = [resolved.expression];
                response = await aggregateMetricMatrix({
                  client: args.client,
                  metrics,
                  expressions,
                  filterInput: [args.entityFilter, args.filterInput],
                  dateScope: trendDateScope,
                  trendByCalendarInterval: trending.interval,
                });
              } else if (metricTypeCheckers.isNormalMetric(metric)) {
                response = await aggregateMetricMatrix({
                  client: args.client,
                  metrics: [toMetricInput(metric)],
                  expressions: [],
                  filterInput: [args.entityFilter, args.filterInput],
                  dateScope: trendDateScope,
                  trendByCalendarInterval: trending.interval,
                });
              } else {
                trendDataMap.set(row.key, undefined);
                return;
              }

              if (response.length === 0) {
                trendDataMap.set(row.key, undefined);
                return;
              }

              const points: TrendPoint[] = response.map((r) => ({
                value: (r[row.metricId] as number | null) ?? 0,
                category: formatTrendCategory({
                  isoDate: r['date'] as string,
                  interval: trending.interval,
                }),
              }));
              const n = points.length;
              const sumX = (n * (n - 1)) / 2;
              const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
              const sumY = points.reduce((acc, p) => acc + p.value, 0);
              const sumXY = points.reduce((acc, p, i) => acc + i * p.value, 0);
              const slope =
                (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
              trendDataMap.set(row.key, { points, isUp: slope >= 0 });
            } catch (e) {
              trendDataMap.set(row.key, undefined);
            }
          }),
        ),
      ]);

      if (!isActive) return;

      const rowResults: RowResult[] = args.allRows.map((row) => {
        if (row.type === 'field') {
          const doc = documentByDataset.get(row.datasetId);
          const raw = doc ? doc[row.field] : undefined;
          const formattedValue =
            raw !== undefined && raw !== null
              ? formatField({
                  field: row.field,
                  dataset: row.datasetId,
                  value: raw,
                })
              : null;
          const label = getFieldLabel({
            field: row.field,
            dataType: row.datasetId,
          });
          return { key: row.key, label, formattedValue, rawValue: raw };
        }
        const metric = args.metricOptions.find((m) => m.id === row.metricId);
        const value = metricResultMap.get(row.key) ?? null;
        const formattedValue =
          value !== null
            ? formatMetric({ value, metricId: row.metricId })
            : null;
        return {
          key: row.key,
          label: metric?.name ?? row.metricId,
          formattedValue,
          trendData: trendDataMap.get(row.key),
        };
      });

      setResults(rowResults);
      setIsLoading(false);
      reportRequest('done');
    };

    run();

    return () => {
      isActive = false;
    };
  }, [
    args,
    resolveMetricForRequest,
    toMetricInput,
    formatField,
    formatMetric,
    getFieldLabel,
    reportRequest,
  ]);

  if (!entityDetails) {
    return (
      <FlexCentered style={{ width: '100%', height: '100%' }}>
        <Typography.Body type={'Body 14'} color={'var(--color-grey-5)'}>
          No Entity selected
        </Typography.Body>
      </FlexCentered>
    );
  }

  if (isLoading) {
    return <CardSkeleton />;
  }

  const renderMode = gadget.renderMode ?? 'cards';
  const columnsPerRow = gadget.columnsPerRow ?? 1;

  const rowItems = gadget.rows.map((row) => {
    const result = results.find((r) => r.key === row.key);
    if (!result) return null;
    return (
      <RowItem
        key={row.key}
        row={row}
        result={result}
        renderMode={renderMode}
        rowHeight={rowHeight}
        formatMetric={formatMetric}
      />
    );
  });

  if (renderMode === 'grid') {
    return (
      <div style={{ overflowY: 'auto', height: '100%' }}>
        <Table>
          <tbody>{rowItems}</tbody>
        </Table>
      </div>
    );
  }

  return (
    <div
      style={{
        overflowY: 'auto',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)`,
        gap: 8,
        padding: 8,
        paddingBottom: 48,
        alignContent: 'start',
      }}
    >
      {rowItems}
    </div>
  );
};

export default EntityInformationTableContent;
